import React from 'react';

import { capitalize } from '@mui/material';

import Bold from '@components/elements/Bold';
import MethodsSectionTitle from '@components/experiments/methods/MethodsSectionTitle';
import ProseList from '@components/ProseList';

import { CellSummaryAnalysis } from '@models/analysis/CellSummaryAnalysis';
import { AnalysisParameters, CellSummaryAnalysisParameters } from '@models/AnalysisParameters';
import AnnotationSet from '@models/Annotation';
import Plot from '@models/Plot';
import CellScatterPlotDisplayOption, {
    getCellCoordinateTypeDisplayName,
} from '@models/plotDisplayOption/CellScatterPlotDisplayOption';

import { formatStringToNumberWithSeparator, formatTableHeader } from '@util/StringUtil';

type Props = { plot: Plot; analysisParameters: AnalysisParameters | null; annotationSets: AnnotationSet[] | null };
const CellSummaryAnalysisMethodsSection = ({ plot, analysisParameters, annotationSets }: Props) => {
    const analysis = plot.analysis as CellSummaryAnalysis;
    const display = plot.display as CellScatterPlotDisplayOption;
    if (!analysis) {
        return <div>No analysis was found.</div>;
    }

    const parameters = analysisParameters as CellSummaryAnalysisParameters;
    const groupingVariables: string[] = [];
    if (analysis.annotation_set_id) {
        const annotationSetName =
            annotationSets?.find((set) => set.uuid === analysis.annotation_set_id)?.display_name ?? '';
        groupingVariables.push(capitalize(formatTableHeader(annotationSetName)));
    }
    if (analysis.variable_ids) {
        analysis.variable_ids.forEach((variableId) => {
            const variableName = parameters.variables.find((v) => v.uuid === variableId)?.display_name ?? '';
            groupingVariables.push(capitalize(formatTableHeader(variableName)));
        });
    }
    if (analysis.latent_variable_id) {
        const latentVariableName =
            parameters.latent_variables.find((v) => v.uuid === analysis.latent_variable_id)?.display_name ?? '';
        groupingVariables.push(capitalize(formatTableHeader(latentVariableName)));
    }

    return (
        <section>
            <section>
                <MethodsSectionTitle>Cell summary analysis</MethodsSectionTitle>
                <p className="mb-6">
                    Cells were grouped and counted based on the following cluster annotation sets, variables, and/or
                    latent variables: <ProseList items={groupingVariables} />.
                </p>
            </section>
            {display.display_type === 'cell_scatter_plot' ? (
                <section>
                    <MethodsSectionTitle>Cell scatter plot</MethodsSectionTitle>
                    <p className="mb-6">
                        Cell scatter plot using{' '}
                        <Bold>
                            {getCellCoordinateTypeDisplayName(
                                (display as CellScatterPlotDisplayOption).cell_coordinate_type,
                            )}
                        </Bold>
                        -defined coordinates. Each point represents a single cell, colored by:{' '}
                        <ProseList items={groupingVariables} />.
                    </p>
                    {parameters?.cell_count > 100_000 ? (
                        <p className="mb-6">
                            To allow for efficient visualization, results data was downsampled to a maximum of{' '}
                            <Bold>{formatStringToNumberWithSeparator(display.downsampling_num as number)}</Bold> cells
                            using a seed of{' '}
                            <Bold>{formatStringToNumberWithSeparator(display.downsampling_seed as number)}</Bold>. Note
                            that downsampling might result in loss of detail or resolution, and can affect the density,
                            separation, and even presence of clusters in a plot. If your resulting cell scatter plot is
                            missing cells/clusters of interest, try re-running the analysis using a different
                            downsampling seed. To plot all cells in your dataset, check out the low-code{' '}
                            <Bold>Cell summary</Bold> recipe.
                        </p>
                    ) : null}
                </section>
            ) : null}
            {display.display_type === 'pie_chart' ? (
                <section>
                    <MethodsSectionTitle>Pie chart</MethodsSectionTitle>
                    <p className="mb-6">
                        Pie chart showing the distribution of cells across: <ProseList items={groupingVariables} />.
                        Each slice represents a group, with proportions corresponding to the number of cells in each
                        category.
                    </p>
                </section>
            ) : null}
        </section>
    );
};

export default CellSummaryAnalysisMethodsSection;
